import { motion, useMotionValue, animate } from 'framer-motion';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import PlayerCard, { type PlayerCardProps } from '@/components/cards/PlayerCard';
import CarouselCard, { SELECTED_BORDER } from '@/components/reveal/CarouselCard';
import { useFitToHeight } from '@/utils/useFitToHeights';

const CTA = {
  width: 214,
  height: 44.58,
  borderRadius: 33.44,
  border: '1.67px solid #FFFFFF',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #E0E0E0 100%)',
  boxShadow: '0px 6.69px 6.69px 0px #00000059',
} as const;

/* Vertical stack, measured from the bottom of the design box upward. */
const CTA_BOTTOM = 56;

/* Gap from the CTA up to the bottom of the card. The comp has no logo between them. */
const CARD_GAP = 130;
const CARD_BOTTOM = CTA_BOTTOM + CTA.height + CARD_GAP;
/** Scale of the front card. PlayerCard's intrinsic box is 110×148. */
const CARD_SCALE = 2;
/** PlayerCard's own height, scaled — the card grows upward from CARD_BOTTOM. */
const CARD_HEIGHT = 148 * CARD_SCALE;
/** Dots sit just under the card's bottom edge. */
const DOTS_BOTTOM = CARD_BOTTOM - 28;

/*
  Side cards sit behind the front one and are clipped by the screen edge. Scale
  is a fraction of CARD_SCALE; offset is the distance between adjacent slots.
*/
const NEIGHBOUR_SCALE = 0.73;
const NEIGHBOUR_OFFSET = 150;

/* Matches LobbyView's round-swipe feel. */
const SWIPE_EASE = [0.32, 0.72, 0, 1] as const;
const SWIPE_DURATION = 0.28;
/** Past this many px the swipe commits; below it the cards ease back. */
const SWIPE_THRESHOLD = 60;

const HEADING_GAP = 44;
const HEADING_BOTTOM = CARD_BOTTOM + CARD_HEIGHT + HEADING_GAP;
const HEADING_HEIGHT = 35.27;

/* Sub line sits directly beneath the heading, inside HEADING_GAP. */
const SUB_GAP = 8;
const SUB_HEIGHT = 15;
const SUB_BOTTOM = HEADING_BOTTOM - SUB_GAP - SUB_HEIGHT;

const DATE_GAP = 4;
const DATE_BOTTOM = HEADING_BOTTOM + HEADING_HEIGHT + DATE_GAP;
/** Rendered height of the date line, so the pack can be anchored above it. */
const DATE_HEIGHT = 15;

/*
  Intrinsic height of the whole composition, pack down to CTA. At or above this
  the layout renders as designed; below it the whole box scales down together.
*/
const DESIGN_HEIGHT = 844;

/*
  The pack is anchored from the bottom like everything else, so its distance to
  the heading is fixed at every screen size. The negative gap tucks it behind
  the date and heading, which paint over it (they come later in the DOM).
*/
const PACK_WIDTH = 100.33;
const PACK_GAP = -14;
const PACK_BOTTOM = DATE_BOTTOM + DATE_HEIGHT + PACK_GAP;
const PACK_ROTATION = -4.07;
const PACK_SHADOW = 'drop-shadow(0px 3.88px 6.59px #00000073)';

const TEXT_SHADOW = '0 1px 3px rgba(0,0,0,0.7), 0 0 10px rgba(0,0,0,0.45)';

// Month, day and year in the app's language; the design has no comma.
const DATE_FORMAT = new Intl.DateTimeFormat(i18n.language, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const formatRevealDate = (date: Date) => DATE_FORMAT.format(date).replace(',', '');

/**
 * One pre-drawn candidate card. `isSelected` carries the committed pick, so a
 * player returning to an unfinished reveal lands on the card they chose —
 * mirroring daily-reveal's CardSelectView.
 */
export type RevealCandidate = {
  /** Candidate id — what a select call posts back. */
  id: string;
  isSelected: boolean;
  card: PlayerCardProps;
};

export type RevealScreenProps = {
  /** Dismisses the screen and returns to the lobby. */
  onContinue: () => void;
  /**
   * The pre-drawn candidates for this slot. Empty while the reveal round-trip
   * is in flight (or for demo cards), when a face-down placeholder shows.
   */
  cards?: RevealCandidate[];
  /**
   * Commits the tapped candidate. Tapping selects immediately — the same
   * behaviour as daily-reveal's CardSelectView, no separate confirm step.
   */
  onSelectCandidate?: (id: string) => void;
  /** Shown in place of the heading when a mutation fails */
  error?: string | null;
};

/** Post-video reveal screen: the pre-drawn candidates over the stadium backdrop. */
export default function RevealScreen({
  onContinue,
  cards = [],
  onSelectCandidate,
  error,
}: RevealScreenProps) {
  const { t } = useTranslation();
  const selectedIndex = cards.findIndex(c => c.isSelected);
  const [activeIndex, setActiveIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);
  /*
    Two-step commit, matching the comp: tapping previews a candidate (green
    border + badge, CTA becomes "Lock in Player") and only the CTA commits it.
    Prevents an accidental tap during a swipe from locking a player in.
  */
  const [previewId, setPreviewId] = useState<string | null>(null);
  const locked = cards.find(c => c.isSelected) ?? null;
  const isLocked = locked !== null;
  const previewIndex = cards.findIndex(c => c.id === previewId);
  const hasPreview = previewIndex >= 0;

  // Maps an unbounded position onto a card index, so the carousel loops in
  // both directions. `activeIndex` itself is never wrapped: keeping it
  // continuous is what keeps each card's key stable across a wrap.
  const getIndex = (base: number, offset: number) =>
    cards.length === 0 ? 0 : (((base + offset) % cards.length) + cards.length) % cards.length;

  const dragX = useMotionValue(0);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isHorizontalDrag = useRef<boolean | null>(null);
  const settleControls = useRef<ReturnType<typeof animate> | null>(null);
  /** dragX when the current gesture began, so an interrupted settle doesn't jump. */
  const dragOrigin = useRef(0);

  const fitScale = useFitToHeight(DESIGN_HEIGHT);

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    settleControls.current?.stop();
    settleControls.current = null;
    /*
      A settle that was more than half-way to the next card is committed right
      now, and the offset rebased by a full slot so nothing moves on screen.
      Without this the index stays behind while dragX keeps its accumulated
      offset, so the clamp leaves almost no travel and swipes can't chain.
    */
    const current = dragX.get();
    if (Math.abs(current) >= NEIGHBOUR_OFFSET / 2) {
      const dir: -1 | 1 = current < 0 ? 1 : -1;
      flushSync(() => {
        setActiveIndex(i => i + dir);
        setPreviewId(null);
      });
      dragX.set(current + dir * NEIGHBOUR_OFFSET);
    }

    dragOrigin.current = dragX.get();
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalDrag.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;

    // Lock the axis on the first meaningful movement, so a vertical scroll
    // doesn't drag the carousel sideways.
    if (isHorizontalDrag.current === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      isHorizontalDrag.current = Math.abs(dx) > Math.abs(dy);
      // Clear as soon as the swipe starts, not when it lands — otherwise the
      // green border rides the card out to the side before blinking off.
      if (isHorizontalDrag.current) setPreviewId(null);
    }
    if (!isHorizontalDrag.current) return;

    /*
      Travel is relative to where the carousel already was, so catching a
      settling animation continues from that point instead of jumping. One slot
      of travel, then damped — a long drag still can't skip a card, while the
      settle animation stays free to overshoot.
    */
    const total = dragOrigin.current + dx;
    const max = NEIGHBOUR_OFFSET;
    const clamped =
      Math.abs(total) <= max ? total : Math.sign(total) * (max + (Math.abs(total) - max) * 0.2);
    dragX.set(clamped);
  };

  const settle = (dir: -1 | 0 | 1) => {
    if (dir === 0) {
      settleControls.current = animate(dragX, 0, {
        duration: SWIPE_DURATION,
        ease: [...SWIPE_EASE],
        onComplete: () => {
          settleControls.current = null;
        },
      });
      return;
    }
    settleControls.current = animate(dragX, -dir * NEIGHBOUR_OFFSET, {
      type: 'spring',
      stiffness: 320,
      damping: 34,
      restDelta: 0.5,
      restSpeed: 10,
      onComplete: () => {
        flushSync(() => {
          setActiveIndex(current => current + dir);
          setPreviewId(null);
        });
        dragX.set(0);
        settleControls.current = null;
      },
    });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    const dx = dragX.get();
    touchStartX.current = null;
    touchStartY.current = null;
    const wasHorizontal = isHorizontalDrag.current;
    isHorizontalDrag.current = null;

    if (!wasHorizontal || cards.length < 2 || Math.abs(dx) < SWIPE_THRESHOLD) {
      settle(0);
      return;
    }
    settle(dx < 0 ? 1 : -1);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 touch-pan-y bg-[#161616]"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-cover bg-bottom bg-no-repeat"
        style={{ backgroundImage: 'url(/field.png)' }}
      />

      {/*
          Outside the scaled design box so it keeps a constant hit area on short
          screens. Always available: a failed reveal or selection would otherwise
          leave no way off this screen.
        */}
      <button
        type="button"
        onClick={onContinue}
        aria-label={t('app.close')}
        className="absolute right-[16px] top-[16px] z-30 flex h-[32px] w-[32px] items-center justify-center rounded-full bg-black/40 active:scale-95"
      >
        <img src="/cross.svg" alt="" className="h-[14px] w-[14px]" />
      </button>
      {/*
        Fixed-height design box, scaled to fit. Children keep their
        bottom-anchored offsets, so the composition stays proportionally
        identical at every height instead of the top elements colliding on
        short screens.
      */}
      <div
        className="absolute inset-x-0 top-1/2 origin-center"
        style={{
          height: DESIGN_HEIGHT,
          marginTop: -DESIGN_HEIGHT / 2,
          transform: `scale(${fitScale})`,
        }}
      >
        <img
          src="/pack.png"
          alt=""
          className="absolute left-1/2 max-w-none"
          style={{
            bottom: PACK_BOTTOM,
            width: PACK_WIDTH,
            transform: `translateX(-50%) rotate(${PACK_ROTATION}deg)`,
            // Blurred and dimmed so it reads as depth behind the heading — the
            // same trick the side cards use to sit back from the front one.
            filter: `blur(.6px) brightness(0.8) ${PACK_SHADOW}`,
          }}
        />

        <p
          className="absolute inset-x-0 font-poppins text-center text-white"
          style={{
            bottom: DATE_BOTTOM,
            fontWeight: 600,
            fontSize: 11,
            lineHeight: '15px',
            letterSpacing: 0,
            textShadow: TEXT_SHADOW,
          }}
        >
          {formatRevealDate(new Date())}
        </p>

        <h1
          className="absolute inset-x-0 font-poppins text-center text-white"
          style={{
            bottom: HEADING_BOTTOM,
            fontWeight: 600,
            fontSize: 25.86,
            lineHeight: '35.27px',
            letterSpacing: 0,
            textShadow: TEXT_SHADOW,
          }}
        >
          {error ? error : isLocked ? t('reveal.lockedTitle') : t('reveal.title')}
        </h1>

        <p
          className="absolute inset-x-0 font-poppins text-center text-white/80"
          style={{
            bottom: SUB_BOTTOM,
            fontWeight: 500,
            fontSize: 12,
            lineHeight: `${SUB_HEIGHT}px`,
            letterSpacing: 0,
            textShadow: TEXT_SHADOW,
          }}
        >
          {isLocked
            ? t('reveal.lockedSub')
            : hasPreview
              ? t('reveal.selectedSub')
              : t('reveal.swipeSub')}
        </p>

        {/*
          Five slots so the outer positions are filled mid-drag; the far two are
          hidden by opacity. Stacked back-to-front by z-index, not DOM order.
        */}
        {isLocked ? (
          /* Locked: just the chosen card, centred, no siblings and no drag. */
          <div className="absolute left-1/2 z-10 -translate-x-1/2" style={{ bottom: CARD_BOTTOM }}>
            <div style={{ transform: `scale(${CARD_SCALE})`, transformOrigin: 'bottom center' }}>
              <PlayerCard {...locked.card} borderColor={SELECTED_BORDER} />
            </div>
          </div>
        ) : cards.length > 0 ? (
          /*
            Five slots so the outer positions are filled mid-drag; the far two
            are hidden by opacity. Stacked back-to-front by z-index, not DOM order.
          */
          (cards.length === 1 ? [0] : [-2, -1, 0, 1, 2]).map(slot => {
            const absolute = activeIndex + slot;
            const candidate = cards[getIndex(absolute, 0)];
            return (
              <CarouselCard
                key={`${candidate.id}:${Math.floor(absolute / cards.length)}`}
                slot={slot}
                card={candidate.card}
                dragX={dragX}
                step={NEIGHBOUR_OFFSET}
                frontScale={CARD_SCALE}
                sideScale={NEIGHBOUR_SCALE}
                bottom={CARD_BOTTOM}
                isSelected={candidate.id === previewId}
                showBadge={candidate.id === previewId}
                onSelect={() => setPreviewId(candidate.id)}
              />
            );
          })
        ) : (
          <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: CARD_BOTTOM }}>
            <div style={{ transform: `scale(${CARD_SCALE})`, transformOrigin: 'bottom center' }}>
              {/* Reveal response not landed yet — hold the face-down state. */}
              <PlayerCard state="upcoming" />
            </div>
          </div>
        )}

        {/* Pagination dots — one per candidate, tap to jump. */}
        {!isLocked && cards.length > 1 && (
          <div
            className="absolute inset-x-0 z-20 flex items-center justify-center gap-[6px]"
            style={{ bottom: DOTS_BOTTOM }}
          >
            {cards.map((candidate, i) => (
              <button
                key={candidate.id}
                type="button"
                aria-label={t('reveal.showPlayer', { n: i + 1 })}
                onClick={() => {
                  setActiveIndex(current => current - getIndex(current, 0) + i);
                  setPreviewId(null);
                }}
                className="w-[6px] h-[6px] rounded-full transition-colors"
                style={{ background: i === getIndex(activeIndex, 0) ? '#FFFFFF' : '#FFFFFF4D' }}
              />
            ))}
          </div>
        )}

        {isLocked || hasPreview ? (
          <button
            type="button"
            onClick={isLocked ? onContinue : () => onSelectCandidate?.(cards[previewIndex].id)}
            className="absolute left-1/2 z-20 -translate-x-1/2 flex items-center justify-center font-poppins transition-transform duration-150 active:scale-[0.98]"
            style={{
              bottom: CTA_BOTTOM,
              ...CTA,
              fontWeight: 600,
              fontSize: 16,
              lineHeight: '100%',
              letterSpacing: 0,
              color: '#000000',
            }}
          >
            {isLocked ? t('reveal.viewSquad') : t('reveal.lockIn')}
          </button>
        ) : (
          /* Frame 1: a hint, not a control — there's nothing to action yet. */
          <div
            className="absolute inset-x-0 z-20 flex items-center justify-center gap-[8px] font-poppins text-white"
            style={{ bottom: CTA_BOTTOM + 10, fontWeight: 600, fontSize: 15 }}
          >
            <span aria-hidden>👆</span>
            {t('reveal.tapHint')}
          </div>
        )}
      </div>
    </motion.div>
  );
}
